import { FlowState } from '@de/providers/dynamicExperienceReducer';
import { Option, Step } from '@de/types/types';
import { getOptionByCustomField } from '@de/utils/getOptionByCustomField';
import { isPii } from '@de/utils/isPii';

/** Matches T-01 / Layer 1 allowlist cap for `entryPoint.forwardFields`. */
export const MAX_FORWARD_FIELDS = 15;

/** Guard against pathological option values in URL query params. */
export const MAX_CONTEXT_PARAM_VALUE_LENGTH = 200;

/**
 * Maps allowlisted non-PII `customField` answers to Discover eject
 * `ctx_*` query params. Opt-in: empty / undefined allowlist ⇒ `{}`.
 *
 * Resolves via `getOptionByCustomField` only (never LFA-keyed APIs).
 * Prefers `slug` over `value`; never emits labels (`.text`).
 */
export function buildContextParams(
  flowState: FlowState,
  steps: Step[],
  forwardFields: string[] | undefined,
): Record<string, string> {
  if (!forwardFields || forwardFields.length === 0) {
    return {};
  }

  const params: Record<string, string> = {};

  for (const rawField of forwardFields) {
    if (Object.keys(params).length >= MAX_FORWARD_FIELDS) {
      break;
    }

    if (typeof rawField !== 'string') {
      continue;
    }
    const field = rawField.trim();
    if (!field) {
      continue;
    }

    const step = steps.find((s) => s.customField === field);
    const option = getOptionByCustomField(field, steps, flowState);
    // Multi-`formKey` Configurable Steps carry `customField` (and their
    // own `leadFieldAssociated`) per-option rather than on the parent
    // `Step` -- the step-level commit intentionally omits
    // `leadFieldAssociated` for these (see `translateFormStateChange`).
    // Fall back to the matched option's own association when there is
    // no step-level match.
    const matchedOptionLfa = !Array.isArray(option)
      ? option?.leadFieldAssociated
      : undefined;
    // Defense-in-depth: OR LFA and customField. A non-PII customField can
    // still bind to a PII lead field (e.g. "contactHandle" + LFA "email"),
    // and the inverse misconfig (PII customField + non-PII LFA) must also
    // be blocked.
    const stepName = step?.stepName ?? field;
    const lfaRaw = step?.leadFieldAssociated ?? matchedOptionLfa;
    const lfa =
      lfaRaw != null && String(lfaRaw).length > 0 ? String(lfaRaw) : null;
    if (
      isPii(field, step, stepName) ||
      (lfa !== null && isPii(lfa, step, stepName))
    ) {
      continue;
    }

    const value = optionToCtxValue(option);
    if (value === null) {
      continue;
    }
    if (value.length > MAX_CONTEXT_PARAM_VALUE_LENGTH) {
      continue;
    }

    params[`ctx_${field}`] = value;
  }

  return params;
}

/**
 * Slug-first string for URL params. Multi-select joins with `,`.
 * Returns null when missing / empty (no label fallback).
 */
function optionToCtxValue(
  option: Option | Option[] | undefined,
): string | null {
  if (!option) {
    return null;
  }

  if (Array.isArray(option)) {
    const parts = option
      .map((opt) => singleOptionCtxValue(opt))
      .filter((part): part is string => part !== null);
    if (parts.length === 0) {
      return null;
    }
    return parts.join(',');
  }

  return singleOptionCtxValue(option);
}

function singleOptionCtxValue(option: Option): string | null {
  const raw = option.slug || option.value;
  if (raw === null || raw === undefined) {
    return null;
  }
  const value = String(raw).trim();
  return value.length > 0 ? value : null;
}
